// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from "react";
import ModalService, { ModalDestructor } from "../nor/ui/services/ModalService";
import ModalType from "../nor/ui/services/types/ModalType";
import LogService from "../nor/ts/LogService";
import FormControls from "../nor/ui/components/saveControls/FormControls";
import Observer, { ObserverCallback, ObserverDestructor } from "../nor/ts/Observer";

const LOG = LogService.createLogger('FormSaveControlsModal');

export enum FormSaveModalEvent {

    OPEN   = "FormSaveModal:open",
    CANCEL = "FormSaveModal:cancel",
    SAVE   = "FormSaveModal:save"

}

export type FormSaveModalDestructor = ObserverDestructor;


export class FormSaveModal {

    private static _saveModal : ModalDestructor | undefined  = undefined;
    private static _observer  : Observer<FormSaveModalEvent> = new Observer<FormSaveModalEvent>("FormSaveModal");


    public static Event = FormSaveModalEvent;

    public static on (
        name: FormSaveModalEvent,
        callback: ObserverCallback<FormSaveModalEvent>
    ): FormSaveModalDestructor {
        return this._observer.listenEvent(name, callback);
    }

    public static destroy (): void {
        LOG.debug(`destroy: Closing modal`)
        this.closeModal();
        LOG.debug(`destroy: Closing observer`)
        this._observer.destroy();
    }

    public static isOpen () : boolean {
        return this._saveModal !== undefined;
    }

    public static closeModal () {
        if (this._saveModal) {
            LOG.debug(`closeModal: Closing modal`);
            this._saveModal();
            this._saveModal = undefined;
        } else {
            LOG.debug(`closeModal: Modal was already closed`);
        }
    }

    public static openModal (hasErrors : boolean) {

        // FIXME: Handle state where the modal was already open with a warning line and do not re-open

        LOG.debug(`openModal: Closing possible previous modal`);
        this.closeModal();

        LOG.debug(`openModal: Opening modal`);

        const cancelAction = () => {
            LOG.debug(`Closing modal through cancel event`);
            this._observer.triggerEvent(FormSaveModalEvent.CANCEL);
            this.closeModal();
        };

        const submitAction = () => {
            LOG.debug(`Closing modal through submit event`);
            this._observer.triggerEvent(FormSaveModalEvent.SAVE);
            this.closeModal();
        };

        this._saveModal = ModalService.createModal(
            (
                <FormControls
                    hasErrors={hasErrors}
                    cancelLabel={'Cancel'}
                    cancel={cancelAction}
                    submitLabel={'Save'}
                    submit={submitAction}
                >{hasErrors ? 'Could not save changes. Please try again.' : 'Save changes'}</FormControls>
            ),
            ModalType.BOTTOM,
            false,
            false
        );

        this._observer.triggerEvent(FormSaveModalEvent.OPEN);

    }

}

export default FormSaveModal;
